import { z } from "zod";
import { paginationSchema } from "./pagination";

/** Template notification types. Domain-agnostic. */
export const notificationTypeSchema = z.enum(["info", "success", "warning", "system"]);

/** Inferred type for a notification type value. */
export type NotificationType = z.infer<typeof notificationTypeSchema>;

/** Reusable UUID identifier schema for notification-scoped inputs. */
export const notificationIdSchema = z.object({
  id: z.string().uuid("Invalid notification ID"),
});

/** Schema for creating a new notification. Used by server-side helpers. */
export const createNotificationSchema = z.object({
  userId: z.string().min(1, "User ID is required").max(255),
  type: notificationTypeSchema.default("info"),
  title: z.string().min(1, "Title is required").max(200, "Title must be 200 characters or less"),
  message: z.string().max(2000, "Message must be 2000 characters or less").optional(),
  linkUrl: z.string().url("Invalid URL").max(500, "Link must be 500 characters or less").optional(),
});

/** Schema for marking a single notification as read. */
export const markReadSchema = notificationIdSchema;

/**
 * Schema for listing notifications. Pagination + optional unread-only filter.
 * Extends the reusable `paginationSchema` from this package.
 */
export const listNotificationsSchema = paginationSchema.extend({
  unreadOnly: z.boolean().default(false),
});

/** Schema for upserting a notification preference row. */
export const updatePreferenceSchema = z.object({
  type: notificationTypeSchema,
  enabled: z.boolean(),
});

/** Inferred type for create notification input. */
export type CreateNotificationInput = z.infer<typeof createNotificationSchema>;

/** Inferred type for mark-read input. */
export type MarkReadInput = z.infer<typeof markReadSchema>;

/** Inferred type for list notifications input. */
export type ListNotificationsInput = z.infer<typeof listNotificationsSchema>;

/** Inferred type for update preference input. */
export type UpdatePreferenceInput = z.infer<typeof updatePreferenceSchema>;
